/**
 * QSL Sync Page
 * 
 * Page for syncing user data from SDPPI.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Loader2, XCircle, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { userService } from "@/services/userService";
import { toast } from "sonner";
import { formatDate } from "./utils/userDetailUtils";
import { formatIarType } from "./utils/statusUtils";
import { useCreateQSLRecord } from "./hooks/useQSLRecords";
import { cn } from "@/lib/utils";

function DetailRow({ label, value, mono, children }: { label: string; value?: string | null; mono?: boolean; children?: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell className="text-sm text-muted-foreground w-[180px] py-2.5">{label}</TableCell>
      <TableCell className={cn("text-sm font-medium py-2.5", mono && "font-mono")}>
        {children ?? (value || <span className="text-muted-foreground">-</span>)}
      </TableCell>
    </TableRow>
  );
}

export function QSLSyncPage() {
  // Set page title
  React.useEffect(() => {
    document.title = "QSL Sync - ORARI";
  }, []);

  const { user } = useAuth();
  const { createQSLRecord, creating } = useCreateQSLRecord();

  // Sync tab state
  const [callsign, setCallsign] = React.useState("");
  const [syncLoading, setSyncLoading] = React.useState(false);
  const [syncError, setSyncError] = React.useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = React.useState<{
    message: string;
    user: {
      id: string;
      callsign: string;
      nama_lengkap: string;
      email: string;
      id_client: string;
      jenis_kelamin?: string;
      kewarganegaraan?: string;
      nri?: string;
      no_hp?: string;
      jabatan_pekerjaan?: string;
      alamat?: string;
      provinsi?: string;
      kabupaten?: string;
      iar?: string;
      tanggal_terbit?: string | null;
      masa_laku?: string | null;
      tipe_iar?: string;
      jenis_permohonan?: string;
      status_callsign?: string;
      status_keanggotaan?: number;
      no_kta?: string;
      masa_laku_kta?: string | null;
      foto?: string;
      updatedAt?: string | null;
      createdAt?: string | null;
    };
  } | null>(null);

  // Handler to save synced callsign as QSL record
  const handleSaveAsQSLRecord = async () => {
    if (!user?.id || !syncSuccess?.user?.callsign) {
      return;
    }

    try {
      await createQSLRecord(user.id, syncSuccess.user.callsign);
      toast.success("Logbook record berhasil disimpan", {
        description: `Logbook dengan ${syncSuccess.user.callsign} telah dicatat`,
      });
    } catch (err) {
      // Error is already handled by the hook's toast
    }
  };

  // Sync tab handlers
  const handleSync = async () => {
    if (!callsign.trim()) {
      setSyncError("Callsign tidak boleh kosong");
      return;
    }

    setSyncLoading(true);
    setSyncError(null);
    setSyncSuccess(null);

    try {
      const result = await userService.syncQslByCallsign(callsign.trim());
      setSyncSuccess(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal mensinkronkan data";
      setSyncError(errorMessage);
      toast.error("Sinkronisasi gagal", {
        description: errorMessage,
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !syncLoading) {
      handleSync();
    }
  };

  return (
    <div className="flex-1 space-y-4 p-6">
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">QSL Sync</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sinkronisasi dari SDPPI</CardTitle>
          <CardDescription>
            Masukkan callsign untuk mengambil data terbaru dari SDPPI dan menyinkronkannya ke sistem
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="callsign">Callsign</Label>
            <Input
              id="callsign"
              placeholder="Masukkan callsign (contoh: YB0DX)"
              value={callsign}
              onChange={(e) => {
                setCallsign(e.target.value.toUpperCase());
                setSyncError(null);
                setSyncSuccess(null);
              }}
              onKeyPress={handleKeyPress}
              disabled={syncLoading}
              className="font-mono"
            />
          </div>

          <Button
            onClick={handleSync}
            disabled={syncLoading || !callsign.trim()}
            className="w-full sm:w-auto"
          >
            {syncLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Mencari...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Cari
              </>
            )}
          </Button>

          {syncError && (
            <div className="rounded-md bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-4">
              <div className="flex items-start gap-2">
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                    Error
                  </h4>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    {syncError}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {syncSuccess && syncSuccess.user && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Data Pengguna</CardTitle>
                <CardDescription>
                  Informasi pengguna yang telah disinkronkan dari SDPPI
                </CardDescription>
              </div>
              {user?.id && (
                <Button
                  onClick={handleSaveAsQSLRecord}
                  disabled={creating}
                  variant="outline"
                >
                  {creating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Simpan sebagai Logbook Record
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableBody>
                <TableRow className="bg-muted/30">
                  <TableCell colSpan={2} className="font-semibold text-xs uppercase tracking-wider text-muted-foreground py-2">
                    Informasi Personal
                  </TableCell>
                </TableRow>
                <DetailRow label="Callsign" value={syncSuccess.user.callsign} mono />
                <DetailRow label="Nama Lengkap" value={syncSuccess.user.nama_lengkap} />
                <DetailRow label="Email" value={syncSuccess.user.email} />
                <DetailRow label="Jenis Kelamin" value={syncSuccess.user.jenis_kelamin === "L" ? "Laki-laki" : syncSuccess.user.jenis_kelamin === "P" ? "Perempuan" : syncSuccess.user.jenis_kelamin} />
                <DetailRow label="NRI" value={syncSuccess.user.nri} mono />
                <DetailRow label="Masa Laku KTA" value={formatDate(syncSuccess.user.masa_laku_kta)} />

                <TableRow className="bg-muted/30">
                  <TableCell colSpan={2} className="font-semibold text-xs uppercase tracking-wider text-muted-foreground py-2">
                    Alamat
                  </TableCell>
                </TableRow>
                <DetailRow label="Alamat" value={syncSuccess.user.alamat} />
                <DetailRow label="Provinsi" value={syncSuccess.user.provinsi} />
                <DetailRow label="Kabupaten" value={syncSuccess.user.kabupaten} />

                <TableRow className="bg-muted/30">
                  <TableCell colSpan={2} className="font-semibold text-xs uppercase tracking-wider text-muted-foreground py-2">
                    Informasi IAR
                  </TableCell>
                </TableRow>
                <DetailRow label="Tanggal Terbit" value={formatDate(syncSuccess.user.tanggal_terbit)} />
                <DetailRow label="Masa Laku" value={formatDate(syncSuccess.user.masa_laku)} />
                <DetailRow label="Tipe IAR" value={formatIarType(syncSuccess.user.tipe_iar)} />
                <DetailRow label="Status Callsign" value={syncSuccess.user.status_callsign} />
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

